import SplunkOtelWeb from '@splunk/otel-web'
import SplunkSessionRecorder from '@splunk/otel-web-session-recorder'
import { trace, SpanStatusCode } from '@opentelemetry/api'
import { APPConfig, convertToBoolean } from 'config/appConfig'

let customerContainer = null

function initCustomerContainer() {
  if (!customerContainer) {
    const Customer = require('src/models/Customer/Customer')
    customerContainer = Customer.customerContainer
  }
  return customerContainer
}

/**
 * Initializes the Splunk Session Recorder.
 * This function reads the necessary configuration from the `APPConfig`
 * and initializes the Splunk Session Recorder with the provided realm and RUM access token.
 */
function initSessionRecorder() {
  const { monitoring } = APPConfig?.getAppConfig() || {}
  const { realm, rumAccessToken } = monitoring || {}
  SplunkSessionRecorder.init({
    realm: realm,
    rumAccessToken,
  })
}

/**
 * Initializes the Splunk monitoring and session recording functionality.
 * This function reads the necessary configuration from the `APPConfig` and initializes the Splunk
 * monitoring and session recording based on the provided configuration.
 */
function initSplunk() {
  const { monitoring } = APPConfig?.getAppConfig() || {}
  const {
    enableSplunk,
    enableSessionRecorder,
    realm,
    rumAccessToken,
    applicationName,
    deploymentEnvironment,
  } = monitoring || {}
  const isToEnableSplunk = convertToBoolean(enableSplunk) || false
  const isToEnableSessionRecorder =
    convertToBoolean(enableSessionRecorder) || false

  if (isToEnableSplunk) {
    SplunkOtelWeb.init({
      realm,
      rumAccessToken,
      applicationName,
      deploymentEnvironment, // development, testing, production
    })

    if (isToEnableSessionRecorder) {
      initSessionRecorder()
    }
  }
}

/**
 * Tracks a custom Splunk event with the given event name and attributes.
 * This function creates a new OpenTelemetry span, adds the event details to the span,
 * and logs the event to the console.
 *
 * @param {string} eventName - The name of the custom event to track.
 * @param {object} [attributes={}] - An optional object containing additional attributes to associate with the event.
 *
 * @note
 * IMPORTANT: workflow.name uses only eventName (low cardinality) to prevent MMS explosion.
 * Customer ID is tracked separately as a span attribute for filtering without affecting metrics cardinality.
 * See: https://docs.splunk.com/en-US/gdi/metrics-and-dimensions
 */

function trackSplunkCustomEvent(options) {
  const { eventName, attributes = {} } = options || {}
  const tracer = trace.getTracer('custom-events-tracer')
  const { customerContainer } = require('src/models/Customer/Customer')
  const container = initCustomerContainer()
  const customerResponse =
    container?.profileResponse?.customer || container?.profileResponse
  const userId = customerResponse?.id || ''

  // Use eventName only (low-cardinality) for workflow.name to prevent MMS explosion.
  // Customer ID is tracked separately as a span attribute for filtering.
  const span = tracer.startSpan(eventName, {
    attributes: {
      'workflow.name': eventName,
      ...attributes,
    },
  })

  try {
    span.addEvent(eventName, {
      ...attributes,
      eventTime: new Date().toISOString(),
    })

    // Add customer information as separate span attributes for filtering
    // without affecting metrics cardinality.
    span.setAttribute('customer.id', userId)
    span.setAttribute('customer.type', customerResponse?.type || 'unknown')
    span.setAttribute('customer.tier', customerResponse?.tier || 'standard')

    Object.entries(attributes).forEach(([key, value]) => {
      span.setAttribute(key, value)
    })

    console.warn(
      `Splunk Tracking event: ${eventName} (customer: ${userId})`,
      attributes
    )
  } catch (error) {
    span.recordException(error)
    span.setStatus({ code: SpanStatusCode.ERROR })
  } finally {
    span.end()
  }
}

export { initSplunk, trackSplunkCustomEvent }
